import React, { useContext, useMemo } from 'react';
import { createMemoStyles, safeRest, defaultBaseline, defaultFonts } from '@wds/utils';
import { IconArrowDown, IconArrowUp } from '@wds/icons';
import { getColor, BACKGROUND_SEMANTIC_COLORS, BACKGROUND_COLORS as BACKGROUND_COLORS$1 } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }
    return t;
  };
  return _assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}
var useStyles$5 = createMemoStyles(function (theme) {
  var palette = theme.palette;
  var tableHeaderStyles = {
    wrenchTableHeader: function wrenchTableHeader(_a) {
      var sticky = _a.sticky,
        elevation = _a.elevation;
      return {
        position: sticky ? 'sticky' : null,
        top: sticky ? 0 : null,
        zIndex: (elevation !== null && elevation !== void 0 ? elevation : sticky) ? 2 : null,
        backgroundColor: sticky ? palette.background.light : null,
        boxShadow: sticky ? "1px 0 0 ".concat(palette.border.muted) : 'none'
      };
    }
  };
  return tableHeaderStyles;
});
var TableHeader = function TableHeader(_a) {
  var children = _a.children,
    _b = _a.sticky,
    sticky = _b === void 0 ? false : _b,
    elevation = _a.elevation,
    rest = __rest(_a, ["children", "sticky", "elevation"]);
  var wrenchTableHeader = useStyles$5({
    sticky: sticky,
    elevation: elevation
  }).wrenchTableHeader;
  return React.createElement("thead", _assign({
    className: wrenchTableHeader
  }, safeRest(rest)), children);
};
var TableBody = function TableBody(_a) {
  var children = _a.children,
    rest = __rest(_a, ["children"]);
  return React.createElement("tbody", _assign({}, safeRest(rest)), children);
};

/** Resolves the correct color */
var getMarkedColor$1 = function getMarkedColor$1(color, palette) {
  if (!color || typeof color !== 'string') {
    return palette.primary.main;
  }
  if (color.startsWith('data-')) {
    return palette.data[color.replace('data-', '')];
  }
  return palette[color].main;
};
var useStyles$4 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    border = theme.border,
    spacing = theme.spacing;
  var tableRowStyles = {
    wrenchTableRow: function wrenchTableRow(_a) {
      var color = _a.color,
        disabled = _a.disabled,
        marked = _a.marked,
        selected = _a.selected;
      return {
        opacity: disabled ? '0.5' : null,
        pointerEvents: disabled ? 'none' : null,
        backgroundColor: selected ? palette.background.main : null,
        '&:last-child': {
          '& > td': {
            borderBottom: 'none'
          }
        },
        '& > td': {
          '&:first-child': {
            position: marked ? 'relative' : null,
            '&:before': {
              content: marked ? '""' : null,
              position: marked ? 'absolute' : null,
              left: marked ? '0' : null,
              top: marked ? '0' : null,
              bottom: marked ? '0' : null,
              width: marked ? spacing[1] : null,
              backgroundColor: marked && color ? getMarkedColor$1(color, palette) : 'none',
              borderRight: border.muted
            }
          }
        }
      };
    }
  };
  return tableRowStyles;
});
var TableRow = function TableRow(_a) {
  var children = _a.children,
    _b = _a.color,
    color = _b === void 0 ? undefined : _b,
    _c = _a.disabled,
    disabled = _c === void 0 ? false : _c,
    _d = _a.marked,
    marked = _d === void 0 ? false : _d,
    _e = _a.onClick,
    onClick = _e === void 0 ? undefined : _e,
    _f = _a.selected,
    selected = _f === void 0 ? false : _f,
    rest = __rest(_a, ["children", "color", "disabled", "marked", "onClick", "selected"]);
  var wrenchTableRow = useStyles$4({
    color: color,
    disabled: disabled,
    marked: marked,
    selected: selected
  }).wrenchTableRow;
  return React.createElement("tr", _assign({
    onClick: onClick,
    className: wrenchTableRow
  }, safeRest(rest)), children);
};
var getCellPadding$1 = function getCellPadding$1(spacing, density) {
  switch (density) {
    case 'sm':
      return [spacing[2], spacing[4]];
    case 'lg':
      return [spacing[5], spacing[5]];
    case 'md':
    default:
      return [spacing[4], spacing[4]];
  }
};
var useStyles$3 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    fontSize = theme.type.fontSize,
    spacing = theme.spacing;
  var table = {
    thTypeColor: palette.text.dark,
    thBgColor: palette.transparent.clear,
    bgColor: {
      light: palette.background.light,
      main: palette.background.main
    },
    thDividerLineColor: palette.transparent.clear,
    tdTypeColor: palette.text.dark,
    cellMinHeight: 50,
    fontSize: fontSize.bodySm
  };
  var cellStyles = {
    fontSize: table.fontSize,
    color: table.tdTypeColor,
    minHeight: table.cellMinHeight,
    verticalAlign: 'top',
    boxShadow: "inset 0 -1px 0 0 ".concat(palette.border.muted),
    '& > *': {
      verticalAlign: 'middle'
    }
  };
  var tableStyles = {
    wrenchTableCell: function wrenchTableCell(_a) {
      var numeric = _a.numeric,
        variant = _a.variant,
        noWrap = _a.noWrap,
        density = _a.density,
        maxWidth = _a.maxWidth,
        minWidth = _a.minWidth,
        align = _a.align,
        backgroundColor = _a.backgroundColor;
      return _assign(_assign(_assign(_assign(_assign(_assign(_assign({}, cellStyles), {
        backgroundColor: table.bgColor[backgroundColor],
        textAlign: numeric ? 'right' : align,
        fontVariantNumeric: numeric ? 'tabular-nums' : null,
        width: variant ? '1%' : null,
        padding: getCellPadding$1(spacing, density)
      }), variant === 'bulkActions' && {
        paddingRight: '0'
      }), {
        whiteSpace: noWrap ? 'nowrap' : null
      }), maxWidth && {
        maxWidth: maxWidth
      }), minWidth && {
        minWidth: minWidth
      }), {
        '& > a': {
          color: palette.text.link
        }
      });
    }
  };
  return tableStyles;
});
var TableContext = React.createContext({
  density: 'md'
});
var TableCell = function TableCell(_a) {
  var children = _a.children,
    _b = _a.numeric,
    numeric = _b === void 0 ? false : _b,
    variant = _a.variant,
    scopeProp = _a.scope,
    parent = _a.parent,
    color = _a.color,
    _c = _a.backgroundColor,
    backgroundColor = _c === void 0 ? 'light' : _c,
    _d = _a.marked,
    marked = _d === void 0 ? false : _d,
    _e = _a.noWrap,
    noWrap = _e === void 0 ? false : _e,
    maxWidth = _a.maxWidth,
    minWidth = _a.minWidth,
    _f = _a.align,
    align = _f === void 0 ? 'left' : _f,
    rest = __rest(_a, ["children", "numeric", "variant", "scope", "parent", "color", "backgroundColor", "marked", "noWrap", "maxWidth", "minWidth", "align"]);
  var density = useContext(TableContext).density;
  var wrenchTableCell = useStyles$3({
    numeric: numeric,
    variant: variant,
    color: color,
    backgroundColor: backgroundColor,
    marked: marked,
    density: density,
    noWrap: noWrap,
    maxWidth: maxWidth,
    minWidth: minWidth,
    align: align
  }).wrenchTableCell;
  var isHeaderChild = parent === 'Table.Header';
  var Component = isHeaderChild ? 'th' : 'td';
  var scope = !scopeProp && isHeaderChild ? 'col' : scopeProp;
  return React.createElement(Component, _assign({
    className: wrenchTableCell,
    scope: scope
  }, safeRest(rest)), children);
};

/** Resolves the correct color */
var getMarkedColor = function getMarkedColor(color, palette) {
  if (!color || typeof color !== 'string') {
    return palette.primary.main;
  }
  if (color.startsWith('data-')) {
    return palette.data[color.replace('data-', '')];
  }
  return palette[color].main;
};
var getCellPadding = function getCellPadding(spacing, density, interactive) {
  switch (density) {
    case 'sm':
      return interactive ? [spacing[2], spacing[2]] : [spacing[4], spacing[4]];
    case 'lg':
      return interactive ? [spacing[2], spacing[4]] : [spacing[4], spacing[5]];
    case 'md':
    default:
      return interactive ? spacing[2] : spacing[4];
  }
};
var getFlexAlignment = function getFlexAlignment(numeric, align) {
  if (numeric) {
    return 'flex-end';
  }
  if (align === 'left') {
    return 'flex-start';
  }
  if (align === 'center') {
    return 'center';
  }
  return 'flex-end';
};
var useStyles$2 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    _a = theme.type,
    fontWeight = _a.fontWeight,
    fontSize = _a.fontSize,
    spacing = theme.spacing;
  var table = {
    thTypeColor: palette.background.dark,
    thBgColor: palette.transparent.clear,
    thDividerLineColor: palette.transparent.clear,
    tdTypeColor: palette.text.dark,
    tdBgColor: palette.transparent.clear,
    cellMinHeight: 50,
    fontSize: fontSize.body
  };
  var cellStyles = {
    fontSize: table.fontSize,
    color: table.tdTypeColor,
    verticalAlign: 'middle',
    boxShadow: "inset 0 -1px 0 0 ".concat(palette.border.muted),
    '& > *': {
      verticalAlign: 'middle'
    }
  };
  var stickyBorder = function stickyBorder(sticky) {
    return {
      '&:after': {
        content: '""',
        position: 'absolute',
        left: sticky === 'right' ? '0' : null,
        right: sticky === 'left' ? '0' : null,
        top: '0',
        height: '100%',
        width: '1px',
        backgroundColor: palette.border.muted
      }
    };
  };
  var tableStyles = {
    wrenchTableHeaderCell: function wrenchTableHeaderCell(_a) {
      var numeric = _a.numeric,
        noWrap = _a.noWrap,
        maxWidth = _a.maxWidth,
        minWidth = _a.minWidth,
        marked = _a.marked,
        color = _a.color,
        isSortable = _a.isSortable,
        isSorted = _a.isSorted,
        sticky = _a.sticky,
        elevation = _a.elevation,
        backgroundColor = _a.backgroundColor,
        align = _a.align,
        density = _a.density,
        onClick = _a.onClick;
      var headerCellPosition = function headerCellPosition() {
        if (sticky) {
          return 'sticky';
        }
        if (marked) {
          return 'relative';
        }
        return null;
      };
      return _assign(_assign(_assign(_assign(_assign(_assign(_assign({}, cellStyles), {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        padding: getCellPadding(spacing, density, !!(isSortable || onClick)),
        minHeight: table.cellMinHeight,
        textAlign: numeric ? 'right' : align,
        fontWeight: fontWeight.medium,
        backgroundColor: getColor(palette, backgroundColor, {
          colorType: 'background'
        }),
        color: backgroundColor !== 'dark' ? table.thTypeColor : palette.text.light,
        position: headerCellPosition(),
        left: sticky === 'left' ? 0 : null,
        right: sticky === 'right' ? 0 : null,
        userSelect: isSortable ? 'none' : null,
        whiteSpace: noWrap ? 'nowrap' : null,
        zIndex: (elevation !== null && elevation !== void 0 ? elevation : sticky) ? 1 : null
      }), maxWidth && {
        maxWidth: maxWidth
      }), minWidth && {
        minWidth: minWidth
      }), {
        '&:before': {
          content: '""',
          position: 'absolute',
          left: '0',
          top: '0',
          width: '100%',
          height: spacing[1],
          backgroundColor: marked && color ? getMarkedColor(color, palette) : 'none'
        }
      }), sticky && stickyBorder(sticky)), {
        '&:first-child': {
          borderLeft: 'none'
        },
        '&:hover': {
          '& > button > span:last-child': {
            opacity: 1,
            color: isSorted ? palette.text.dark : palette.text.darkMuted
          }
        }
      });
    },
    wrenchTableSortIcon: function wrenchTableSortIcon(_a) {
      var isSorted = _a.isSorted;
      return {
        display: 'inline-flex',
        pointerEvents: 'none',
        marginLeft: spacing[2],
        opacity: isSorted ? 1 : 0.42,
        color: palette.text.dark
      };
    },
    wrenchTableHeaderContainer: function wrenchTableHeaderContainer(_a) {
      var numeric = _a.numeric,
        align = _a.align;
      return {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        justifyContent: getFlexAlignment(numeric, align)
      };
    },
    wrenchTableHeaderText: {},
    wrenchTableSortButton: function wrenchTableSortButton(_a) {
      var isSorted = _a.isSorted,
        backgroundColor = _a.backgroundColor,
        numeric = _a.numeric,
        density = _a.density,
        align = _a.align;
      return {
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        minHeight: density === 'sm' ? spacing[3] : spacing[6],
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        fontWeight: fontWeight.medium,
        color: backgroundColor !== 'dark' ? table.thTypeColor : palette.text.light,
        justifyContent: getFlexAlignment(numeric, align),
        padding: spacing[2],
        '&:focus': {
          '& > span': {
            opacity: 1,
            color: isSorted ? palette.text.dark : palette.text.darkMuted
          }
        }
      };
    },
    wrenchHeaderCellAddon: {
      marginLeft: spacing[2],
      cursor: 'default'
    }
  };
  return tableStyles;
});
var SortArrow = function SortArrow(_a) {
  var sortDirection = _a.sortDirection,
    wrenchTableSortIcon = _a.wrenchTableSortIcon,
    sortAscendingLabel = _a.sortAscendingLabel,
    sortDescendingLabel = _a.sortDescendingLabel;
  var sortIcon;
  if (sortDirection === 'descending') {
    sortIcon = React.createElement(IconArrowDown, {
      title: sortAscendingLabel
    });
  } else {
    sortIcon = React.createElement(IconArrowUp, {
      title: sortDescendingLabel
    });
  }
  return React.createElement("span", {
    "aria-hidden": "true",
    className: wrenchTableSortIcon
  }, sortIcon);
};
var TableHeaderCell = function TableHeaderCell(_a) {
  var _b = _a.children,
    children = _b === void 0 ? undefined : _b,
    _c = _a.numeric,
    numeric = _c === void 0 ? false : _c,
    _d = _a.scope,
    scopeProp = _d === void 0 ? undefined : _d,
    _e = _a.parent,
    parent = _e === void 0 ? undefined : _e,
    _f = _a.color,
    color = _f === void 0 ? undefined : _f,
    _g = _a.backgroundColor,
    backgroundColor = _g === void 0 ? 'main' : _g,
    _h = _a.marked,
    marked = _h === void 0 ? false : _h,
    _j = _a.isSortable,
    isSortable = _j === void 0 ? false : _j,
    _k = _a.isSorted,
    isSorted = _k === void 0 ? false : _k,
    _l = _a.sortDirection,
    sortDirection = _l === void 0 ? 'ascending' : _l,
    _m = _a.sortAscendingLabel,
    sortAscendingLabel = _m === void 0 ? 'Sort rows in ascending order.' : _m,
    _o = _a.sortDescendingLabel,
    sortDescendingLabel = _o === void 0 ? 'Sort rows in descending order.' : _o,
    _p = _a.noWrap,
    noWrap = _p === void 0 ? false : _p;
  _a.labelSort;
  var maxWidth = _a.maxWidth,
    minWidth = _a.minWidth,
    _r = _a.sticky,
    sticky = _r === void 0 ? undefined : _r,
    _s = _a.elevation,
    elevation = _s === void 0 ? undefined : _s,
    _t = _a.onClick,
    onClick = _t === void 0 ? undefined : _t,
    _u = _a.align,
    align = _u === void 0 ? 'left' : _u,
    _v = _a.interactiveAddon,
    interactiveAddon = _v === void 0 ? undefined : _v,
    rest = __rest(_a, ["children", "numeric", "scope", "parent", "color", "backgroundColor", "marked", "isSortable", "isSorted", "sortDirection", "sortAscendingLabel", "sortDescendingLabel", "noWrap", "labelSort", "maxWidth", "minWidth", "sticky", "elevation", "onClick", "align", "interactiveAddon"]);
  var density = useContext(TableContext).density;
  var _w = useStyles$2({
      numeric: numeric,
      noWrap: noWrap,
      maxWidth: maxWidth,
      minWidth: minWidth,
      marked: marked,
      color: color,
      isSortable: isSortable,
      isSorted: isSorted,
      sticky: sticky,
      backgroundColor: backgroundColor,
      align: align,
      elevation: elevation,
      density: density,
      onClick: onClick
    }),
    wrenchTableHeaderCell = _w.wrenchTableHeaderCell,
    wrenchTableSortIcon = _w.wrenchTableSortIcon,
    wrenchTableSortButton = _w.wrenchTableSortButton,
    wrenchHeaderCellAddon = _w.wrenchHeaderCellAddon,
    wrenchTableHeaderContainer = _w.wrenchTableHeaderContainer,
    wrenchTableHeaderText = _w.wrenchTableHeaderText;
  var isHeaderChild = parent === 'Table.Header';
  var scope = !scopeProp && isHeaderChild ? 'col' : scopeProp;
  var handleOnClick = function handleOnClick(e) {
    var element = e.target;
    var closest = element.closest('[data-component]');
    if (!closest && onClick) {
      onClick(e);
    }
  };
  var ariaLabel;
  if (isSortable) {
    ariaLabel = sortDirection === 'ascending' ? sortAscendingLabel : sortDescendingLabel;
  }
  return React.createElement("th", _assign({
    className: wrenchTableHeaderCell,
    scope: scope,
    "aria-sort": isSorted ? sortDirection : undefined
  }, safeRest(rest)), isSortable || onClick ? React.createElement("button", {
    type: "button",
    onClick: function onClick(e) {
      return handleOnClick(e);
    },
    "aria-describedby": isSorted ? sortDirection : undefined,
    className: wrenchTableSortButton,
    "data-column": rest['data-column'],
    "aria-label": ariaLabel
  }, React.createElement("span", {
    "data-column": rest['data-column'],
    className: wrenchTableHeaderText
  }, children), interactiveAddon && React.createElement("span", {
    "data-component": "headerCellAddon",
    className: wrenchHeaderCellAddon
  }, interactiveAddon), isSortable && React.createElement(SortArrow, {
    sortDirection: sortDirection,
    wrenchTableSortIcon: wrenchTableSortIcon,
    sortAscendingLabel: sortAscendingLabel,
    sortDescendingLabel: sortDescendingLabel
  })) : React.createElement("div", {
    className: wrenchTableHeaderContainer
  }, React.createElement("span", {
    className: wrenchTableHeaderText
  }, children), interactiveAddon && React.createElement("span", {
    className: wrenchHeaderCellAddon
  }, interactiveAddon)));
};
var useStyles$1 = createMemoStyles(function (theme) {
  var palette = theme.palette;
  var tableFooterStyles = {
    wrenchTableFooter: function wrenchTableFooter(_a) {
      var sticky = _a.sticky;
      return {
        position: sticky ? 'sticky' : null,
        bottom: sticky ? 0 : null,
        zIndex: sticky ? 1 : null,
        backgroundColor: sticky ? palette.background.light : null,
        boxShadow: sticky ? "0 -1px 0 ".concat(palette.border.muted) : 'none'
      };
    }
  };
  return tableFooterStyles;
});
var TableFooter = function TableFooter(_a) {
  var children = _a.children,
    _b = _a.sticky,
    sticky = _b === void 0 ? false : _b,
    rest = __rest(_a, ["children", "sticky"]);
  var wrenchTableFooter = useStyles$1({
    sticky: sticky
  }).wrenchTableFooter;
  return React.createElement("tfoot", _assign({
    className: wrenchTableFooter
  }, safeRest(rest)), children);
};
var useStyles = createMemoStyles(function (theme) {
  var tableStyles = {
    wrenchTableContainer: function wrenchTableContainer(_a) {
      var xScroll = _a.xScroll,
        maxHeight = _a.maxHeight;
      return _assign(_assign({}, defaultBaseline), {
        overflowX: xScroll ? 'auto' : null,
        maxHeight: maxHeight !== null && maxHeight !== void 0 ? maxHeight : null,
        overflowY: maxHeight ? 'auto' : null
      });
    },
    wrenchTable: function wrenchTable() {
      return _assign(_assign({}, defaultFonts(theme)), {
        width: '100%',
        borderCollapse: 'collapse'
      });
    }
  };
  return tableStyles;
});
var Table = function Table(_a) {
  var children = _a.children,
    _b = _a.xScroll,
    xScroll = _b === void 0 ? false : _b,
    _c = _a.density,
    density = _c === void 0 ? 'md' : _c,
    _d = _a.maxHeight,
    maxHeight = _d === void 0 ? undefined : _d,
    rest = __rest(_a, ["children", "xScroll", "density", "maxHeight"]);
  var _e = useStyles({
      xScroll: xScroll,
      maxHeight: maxHeight
    }),
    wrenchTableContainer = _e.wrenchTableContainer,
    wrenchTable = _e.wrenchTable;
  var value = useMemo(function () {
    return {
      density: density
    };
  }, [density]);
  return React.createElement(TableContext.Provider, {
    value: value
  }, React.createElement("div", {
    className: wrenchTableContainer
  }, React.createElement("table", _assign({
    className: wrenchTable
  }, safeRest(rest)), children)));
};
Table.Header = TableHeader;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.HeaderCell = TableHeaderCell;
Table.Footer = TableFooter;
var STICKY_SIDES = ['left', 'right'];
var DENSITIES = ['sm', 'md', 'lg'];
var ALIGNMENTS = ['left', 'center', 'right'];
var BACKGROUND_COLORS = __spreadArray(__spreadArray([], BACKGROUND_SEMANTIC_COLORS, true), BACKGROUND_COLORS$1, true);
var CELL_BACKGROUND_COLOR = ['main', 'light'];
export { ALIGNMENTS, BACKGROUND_COLORS, CELL_BACKGROUND_COLOR, DENSITIES, STICKY_SIDES, Table };